<script setup lang="ts">
interface SidenavAccountProps {
    showCloseButton?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<SidenavAccountProps>(), {
    showCloseButton: true
})

// Global sidenav state management
const { isOpen, close } = useSidenav()
const accountOpen = isOpen('account')

// User data and authentication
const { isGuestSession, logout, isLoggedIn } = useUser()

// i18n
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Account navigation links based on Shopware structure
const accountLinks = [
    {
        href: '/account',
        label: 'account.nav.overview',
    },
    {
        href: '/account/profile',
        label: 'account.nav.profile',
    },
    {
        href: '/account/address',
        label: 'account.nav.address',
    },
    {
        href: '/account/orders',
        label: 'account.nav.orders',
    },
    {
        href: '/account/newsletter',
        label: 'account.nav.newsletter',
    }
]

// Handle navigation
const handleNavigation = (href: string) => {
    navigateTo(formatLink(href))
    accountOpen.value = false
}

// Handle logout
const handleLogout = async () => {
    try {
        await logout()
        accountOpen.value = false
        navigateTo(formatLink('/'))
    } catch (error) {
        console.error('Logout failed:', error)
    }
}

// Handle switch to register page
const handleSwitchToRegister = () => {
    navigateTo(formatLink('/account/register'))
    accountOpen.value = false
}
</script>

<template>
    <SidenavOverlay
        v-model="accountOpen"
        direction="right"
        width-class="w-full md:w-[600px]"
    >
        <div class="flex flex-col h-full bg-base-100">
            <!-- Header -->
            <SidenavHeader
                :title="$t('account.nav.title')"
                :show-close-button="showCloseButton"
                :close-aria-label="$t('account.nav.close')"
                @close="close('account')"
            />

            <ClientOnly>
                <!-- Navigation Links for logged in users -->
                <template v-if="isLoggedIn && !isGuestSession">
                    <nav class="flex-grow py-3 px-6 md:px-9" :aria-label="$t('account.nav.title')">
                        <ul class="space-y-4">
                            <li v-for="link in accountLinks" :key="link.href">
                                <FoundationButton
                                    color="secondary"
                                    :aria-label="$t(link.label)"
                                    class="w-full"
                                    @click="handleNavigation(link.href)"
                                >
                                    {{ $t(link.label) }}
                                </FoundationButton>
                            </li>

                            <li>
                                <FoundationButton
                                    color="primary"
                                    class="w-full"
                                    :aria-label="$t('account.logout')"
                                    @click="handleLogout"
                                >
                                    {{ $t('account.logout') }}
                                </FoundationButton>
                            </li>
                        </ul>
                    </nav>
                </template>

                <!-- Login Form for non-authenticated users or guest sessions -->
                <template v-else>
                    <div class="flex-grow overflow-auto md:px-9 px-6 py-6">
                        <AccountLogin @switch-to-register="handleSwitchToRegister" />
                    </div>

                    <!-- Footer with guest session abort if applicable -->
                    <div v-if="isGuestSession" class="py-3 px-6 md:px-9">
                        <FoundationButton
                            color="primary"
                            class="w-full"
                            :aria-label="$t('account.guestAbort')"
                            @click="handleLogout"
                        >
                            <FoundationIcon name="log-out" class="mr-3" />
                            {{ $t('account.guestAbort') }}
                        </FoundationButton>
                    </div>
                </template>

                <!-- Fallback for SSR -->
                <template #fallback>
                    <div class="flex items-center justify-center flex-grow p-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-border" />
                    </div>
                </template>
            </ClientOnly>
        </div>
    </SidenavOverlay>
</template>
